import {AbstractParameter, IntegerParameter} from 'parameters/AbstractParameter.js';
import {RIGHT} from 'constants/hand-types.js';
import {DELETE_STEP} from './constants.js';
import {setEffectParameterValues} from 'actions/index.js';
import ParameterFuture from 'effects/ParameterFuture.js';
import Tone from 'tone';
import Resolution from 'engine/Resolution.js';
import {uuid} from 'uuidv4';

// trigger panning automation 10 millseconds before its supposed to happen
const PANNING_LATENCY = 50;
window.initStepDatas= 0;

class StepData {
    constructor(sequencerEngine, mixSession, context, track) {
        let store = sequencerEngine !== undefined ? sequencerEngine.store : undefined;
        window.initStepDatas++;
        this.track = track;
        this.context = context;
        this.mixSession = mixSession;
        this.sequencerEngine = sequencerEngine;
        this.stepNumber = undefined;
        this.position = 0;
        this.id = uuid();
        this.parameters = [];
        this.on = false;
        this.pLockedParameters = [];
       
        this.hand = RIGHT;

        this.durationParameter = new AbstractParameter(
            'Duration', this, (o, x) => x, {
                min: 0.01,
                max: 64,
                unit: '1/16th',
                defaultValue: null
            });
        this.registerParameter(this.durationParameter, store, mixSession, context);

        this.transposeParameter = new AbstractParameter(
            'Transpose', this, (o, x) => x, {
                min: -32,
                max: 32,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.transposeParameter, store, mixSession, context);

        this.chopParameter = new IntegerParameter(
            'Chop', this, (o, x) => x, {
                min: 1,
                max: 16,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.chopParameter, store, mixSession, context);

        // TODO: remove b/c it's unused
        this.chopDurationParameter = new IntegerParameter(
            'Chop Duration', this, (o, x) => x, {
                min: 1,
                max: 32,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.chopDurationParameter, store, mixSession, context);

        this.panningParameter = new AbstractParameter(
            'Panning', this, (o, x) => x, {
                min: -1,
                max: 1,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.panningParameter, store, mixSession, context);

        this.velocityParameter = new AbstractParameter(
            'Velocity', this, (o, x) => x, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.velocityParameter, store, mixSession, context);

        // the start % offset that we should trigger a sample
        this.startParameter = new AbstractParameter(
            'Start', this, (o, x) => x, {
                min: 0,
                max: 100,
                unit: '%',
                defaultValue: null
            });
        this.registerParameter(this.startParameter, store, mixSession, context);

        this.attackParameter = new AbstractParameter(
            'Attack', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.attackParameter, store, mixSession, context);

        this.releaseParameter = new AbstractParameter(
            'Release', this, (o, x) => x, {
                min: 0,
                max: 10000,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.releaseParameter, store, mixSession, context);

        this.sliceParameter = new IntegerParameter(
            'Slice', this, (o, x) => x, {
                min: 0,
                max: 64,
                unit: '',
                defaultValue: null,
                automatable: false
            });
        this.registerParameter(this.sliceParameter, store, mixSession, context);

        this.pitchAttackParameter = new AbstractParameter(
            'Pitch Attack', this, (o, x) => x, {
                min: 0.01,
                max: 10000,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.pitchAttackParameter, store, mixSession, context);

        this.pitchDecayParameter = new AbstractParameter(
            'Pitch Decay', this, (o, x) => x, {
                min: 0.01,
                max: 10000,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.pitchDecayParameter, store, mixSession, context);

        this.pitchSustainParameter = new AbstractParameter(
            'Pitch Sustain', this, (o, x) => x, {
                min: -10,
                max: 10,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.pitchSustainParameter, store, mixSession, context);

        this.pitchReleaseParameter = new AbstractParameter(
            'Pitch Release', this, (o, x) => x, {
                min: 0.01,
                max: 10000,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.pitchReleaseParameter, store, mixSession, context);

        this.selectedRecordingParameter= new AbstractParameter(
            'Selected Recording', this, (o, x) => x, {
                min: 1,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.selectedRecordingParameter, store, mixSession, context);

        this.monophonicParameter = new AbstractParameter(
            'Monophonic', this, (o, x) => x, {
                min: false,
                max: true,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.monophonicParameter, store, mixSession, context);

        this.reverseParameter = new AbstractParameter(
            'reverse', this, (o, x) => x, {
                min: 0,
                max: 1,
                unit: '',
                defaultValue: null,
                automatable: false
            });
        this.registerParameter(this.reverseParameter, store, mixSession, context);

        this.transposeMatchParameter = new AbstractParameter(
            'Transpose Match', this, (o, x) => x, {
                min: 0,
                max: 20,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.transposeMatchParameter, store, mixSession, context);

        this.paletteParameter = new AbstractParameter(
            'Palette', this, (o, x) => x, {
                min: 0,
                max: 20,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.paletteParameter, store, mixSession, context);

        this.mutateParameter = new AbstractParameter(
            'Mutate', this, (o, x) => x, {
                min: -100,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.mutateParameter, store, mixSession, context);

        this.probabilityParameter = new AbstractParameter(
            'Probability', this, (o, x) => x, {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.probabilityParameter, store, mixSession, context);

        this.auxEventAParameter = new AbstractParameter(
            'Aux Event A', this, (o, x) => x, {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.auxEventAParameter, store, mixSession, context);

        this.auxEventBParameter = new AbstractParameter(
            'Aux Event B', this, (o, x) => x, {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.auxEventBParameter, store, mixSession, context);

        this.auxEventCParameter = new AbstractParameter(
            'Aux Event C', this, (o, x) => x, {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.auxEventCParameter, store, mixSession, context);

        this.auxEventDParameter = new AbstractParameter(
            'Aux Event D', this, (o, x) => x, {
                min: 0,
                max: 100,
                unit: '',
                defaultValue: null
            });
        this.registerParameter(this.auxEventDParameter, store, mixSession, context);

        this.effectAutomations = new EffectAutomations();
        
        // list of other steps to be be played
        // at the same time
        this.polyphonicSteps = [];

        // we define polyphony at a certain "step" (i.e. position in time) as a
        // linked list notes starting at the "head" (which is stored in a StepBucket).
        // Then the "nextStepData" here represents the remaining list in this polyphonic
        // chord.
        this.nextStepData = undefined;
    }
    
    registerParameter(parameter, store, mixSession, context) {
        parameter.setStore(store);

        if (mixSession !== undefined) {
            parameter.setMixSession(mixSession);
        }

        if (context !== undefined) {
            parameter.setContext(context);
        }

        parameter.track = this.track;
        this.parameters.push(parameter);
    }
    
    triggerAutomations(time, track) {
        if (this.panningParameter.get() !== null) {
            track.setPanning(this.panning, time);
        }
        let bulk = {};
        for (let parameter of this.pLockedParameters) {
            bulk = {
                ... bulk,
                ... parameter.setForPLockedStep(this, time)
            };
        }
        if (Object.keys(bulk).length > 0) {
            track.store.dispatch(
                setEffectParameterValues(bulk));
        }

        return this.pLockedParameters;
    }

    get trackData() {
        return this.track !== undefined ? this.track.trackData : this;
    }

    get duration() {
        return this.durationParameter.get() === null ?
            this.trackData.durationParameter.get() :
            this.durationParameter.get();
    }

    get panning() {
        return this.panningParameter.get() === null ?
            this.trackData.panningParameter.get()  :
            this.panningParameter.get();
    }

    set panning(p) {
        this.panningParameter.set(p);
    }

    set duration(d) {
        this.durationParameter.set(d);
    }

    get transpose() {
        return this.transposeParameter.get() === null ?
            this.trackData.transposeParameter.get()  :
            this.transposeParameter.get();
    }

    set transpose(d) {
        this.transposeParameter.set(d);
    }

    get velocity() {
        return this.velocityParameter.get() === null ?
            this.trackData.velocityParameter.get() :
            this.velocityParameter.get();
    }

    set velocity(d) {
        this.velocityParameter.set(d);
    }

    get start() {
        return this.startParameter.get() === null ?
            this.trackData.startParameter.get()  :
            this.startParameter.get();
    }

    set start(d) {
        this.startParameter.set(d);
    }

    get chop() {
        return this.chopParameter.get() === null ?
            this.trackData.chopParameter.get()  :
            this.chopParameter.get();
    }

    set chop(d) {
        this.chopParameter.set(d);
    }

    get chopDuration() {
        return this.chopDurationParameter.get() === null ?
            this.trackData.chopDurationParameter.get()  :
            this.chopDurationParameter.get();
    }

    set chopDuration(d) {
        this.chopDurationParameter.set(d);
    }

    get slice() {
        return this.sliceParameter.get() === null ?
            this.trackData.sliceParameter.get()  :
            this.sliceParameter.get();
    }

    set slice(d) {
        this.sliceParameter.set(d);
    }

    get palette() {
        return this.paletteParameter.get() === null ?
            this.trackData.paletteParameter.get()  :
            this.paletteParameter.get();
    }

    set palette(d) {
        this.paletteParameter.set(d);
    }

    get probability() {
        return this.probabilityParameter.get() === null ?
            this.trackData.probabilityParameter.get()  :
            this.probabilityParameter.get();
    }

    set probability(p) {
        this.probabilityParameter.set(p);
    }

    get auxEventA() {
        return this.auxEventAParameter.get() === null ?
            this.trackData.auxEventAParameter.get()  :
            this.auxEventAParameter.get();
    }

    set auxEventA(p) {
        this.auxEventAParameter.set(p);
    }

    get auxEventB() {
        return this.auxEventBParameter.get() === null ?
            this.trackData.auxEventBParameter.get()  :
            this.auxEventBParameter.get();
    }

    set auxEventB(p) {
        this.auxEventBParameter.set(p);
    }

    get auxEventC() {
        return this.auxEventCParameter.get() === null ?
            this.trackData.auxEventCParameter.get()  :
            this.auxEventCParameter.get();
    }

    set auxEventC(p) {
        this.auxEventCParameter.set(p);
    }

    get auxEventD() {
        return this.auxEventDParameter.get() === null ?
            this.trackData.auxEventDParameter.get()  :
            this.auxEventDParameter.get();
    }

    set auxEventD(p) {
        this.auxEventDParameter.set(p);
    }



    get attack() {
        return this.attackParameter.get() === null ?
            this.trackData.attackParameter.get()  :
            this.attackParameter.get();
    }

    set attack(d) {
        this.attackParameter.set(d);
    }

    get release() {
        return this.releaseParameter.get() === null ?
            this.trackData.releaseParameter.get()  :
            this.releaseParameter.get();
    }

    set release(d) {
        this.releaseParameter.set(d);
    }

    get pitchAttack() {
        return this.pitchAttackParameter.get() === null ?
            this.trackData.pitchAttackParameter.get()  :
            this.pitchAttackParameter.get();
    }

    set pitchAttack(d) {
        this.pitchAttackParameter.set(d);
    }

    get pitchDecay() {
        return this.pitchDecayParameter.get() === null ?
            this.trackData.pitchDecayParameter.get()  :
            this.pitchDecayParameter.get();
    }

    set pitchSustain(d) {
        this.pitchSustainParameter.set(d);
    }

    get pitchSustain() {
        return this.pitchSustainParameter.get() === null ?
            this.trackData.pitchSustainParameter.get()  :
            this.pitchSustainParameter.get();
    }

    set pitchDecay(d) {
        this.pitchDecayParameter.set(d);
    }

    get pitchRelease() {
        return this.pitchReleaseParameter.get() === null ?
            this.trackData.pitchReleaseParameter.get()  :
            this.pitchReleaseParameter.get();
    }

    set pitchRelease(d) {
        this.pitchReleaseParameter.set(d);
    }

    set reverse(r) {
        this.reverseParameter.set(r);
    }

    get reverse() {
        return this.reverseParameter.get() === null ?
            this.trackData.reverseParameter.get()  :
            this.reverseParameter.get();
    }

    set transposeMatch(track) {
        this.transposeMatchParameter.set(track);
    }

    get transposeMatch() {
        return this.transposeMatchParameter.get();
    }

    set mutate(m) {
        this.mutateParameter.set(m);
    }

    get mutate() {
        return this.mutateParameter.get();
    }

    get selectedRecording() {
        return this.selectedRecordingParameter.get() === null ?
            this.trackData.selectedRecordingParameter.get()  :
            this.selectedRecordingParameter.get();
    }

    set selectedRecording(d) {
        this.selectedRecordingParameter.set(d);
    }

    set monophonic(x) {
        this.monophonicParameter.set(x);
    }

    get monophonic() {
        return this.monophonicParameter.get() === null ?
            this.trackData.monophonicParameter.get()  :
            this.monophonicParameter.get();
    }

    // adds a step to the polyphony, unless there is no polyphony
    // then the given step becomes the main step.
    // returns the "new head" of the list of polyphony
    addStep(step) {
        if (this === step || this.id === step.id || this.transpose === step.transpose) {
            return step;
        }

        if (this.nextStepData === undefined) {
            if (!this.on) {
                return step;
            }
            this.nextStepData = step;
        } else {
            this.nextStepData = this.nextStepData.addStep(step);
        }

        return this;
    }

    hasStep(step) {
        if (step === undefined) {
            return false;
        }

        if (this === step || this.id === step.id) {
            return true;
        }

        if (this.nextStepData === undefined) {
            return false;
        }

        return this.nextStepData.hasStep(step);
    }

    // returns the "new head" of the list of polyphony
    deleteStep(step, i=0) {
        if (this === step || this.id === step.id) {
            if (this.nextStepData === undefined) {
                return DELETE_STEP;
            } else {
                let nextStepData = this.nextStepData;
                this.nextStepData = undefined;
                return nextStepData;
            }
        }

        // otherwise keep looking for the desired step
        if (this.nextStepData === undefined) {
            // we did not find it
            return undefined; 
        }

        let tmp = this.nextStepData.deleteStep(step, i+1);
        if (tmp === DELETE_STEP ) {
            this.nextStepData = undefined;
        } else if (tmp !== undefined) {
            this.nextStepData = tmp;
        }
        return this;
    }

    containsStep(step) {
        if (step.id === this.id) {
            return true;
        }

        if (this.nextStepData === undefined ){
            return false;
        }

        return this.containsStep(this.nextStepData);
    }

    getStepDataForTranspose(transpose, clearNull) {
        if (Math.round(this.transpose) == transpose || (clearNull && this.transpose === null)) {
            return this;
        }

        let polyphony = this.getPolyphony();
        let found = -1;
        for (let i in polyphony) {
            let step = polyphony[i];
            if (Math.round(step.transpose) == transpose || (clearNull && step.transpose === null)) {
                found = i;
                break;
            }
        }

        if (found == -1) {
            return undefined;
        }

        let step = polyphony[found];
        return step;
    }

    deleteTranspose(transpose, clearNull) {
        if (this.transpose === transpose || (clearNull && this.transpose === null)) {
            let poly =  this.polyphonicSteps;
            this.clear();
            if (poly.length > 0) {
                this.copyFrom(poly[0]);
                this.polyphonicSteps = poly.slice(1);
            }
        } else {
            // find the poly that has the thing
            let step = this.polyphonicSteps.filter(s => s.transpose == transpose || (clearNull && s.transpose == null));
            if (step.length > 0) {
                let index = this.polyphonicSteps.indexOf(step[0]);
                this.polyphonicSteps.splice(index, 1);
            }
        }
    }

    getPolyphony() {
        let next = this;
        let polyphony = [];
        while (next !== undefined) {
            next.track = this.track;
            polyphony.push(next);
            next = next.nextStepData;
        }

        return polyphony;
    }

    equals(o) {
        return !(this.on != o.on ||
            this.duration != o.duration ||
            this.transpose != o.transpose ||
            this.chop != o.chop ||
            this.chopDuration != o.chopDuration ||
            this.velocity != o.velocity ||
            this.start != o.start ||
            this.panning != o.panning ||
            this.attack != o.attack ||
            this.reverse != o.reverse ||
            this.release != o.release ||
            this.slice != o.slice ||
            this.pitchAttack !== o.pitchAttack ||
            this.pitchDecay !== o.pitchDecay ||
            this.pitchSustain !== o.pitchSutain ||
            this.selectedRecording !== o.selectedRecording ||
                 this.pitchRelease !== o.pitchRelease);
    }

    simpleLoad(json, effects, track, midiEffects) {
        /*
        this.on = json.on;
        if (json.id !== undefined) {
            this.id = json.id;
        }

        this.duration = orNull(json.duration);
        this.panning = orNull(json.panning);
        this.transpose = orNull(json.transpose);
        if (this.on) {
            console.log('transpose =', orNull(json.transpose), json.transpose);
        }
        this.velocity = orNull(json.velocity);
        this.chop = orNull(json.chop);
        this.pitchSustain = orNull(json.pitchSustain);
        this.pitchAttack = orNull(json.pitchAttack);
        this.pitchRelease = orNull(json.pitchRelease);
        this.pitchDecay = orNull(json.pitchDecay);
        this.attack = orNull(json.attack);
        this.release = orNull(json.release);

        this.reverse = orNull(json.reverse);

        if (json.effectAutomations !== undefined) {
            this.loadAutomations(json.effectAutomations, track, effects, midiEffects);
       }

        if ('polyphonicSteps'  in json ) {
            json.polyphonicSteps
                .map(
                    step => {
                        let stepData = new StepData();
                        stepData.stepNumber = this.stepNumber;
                        stepData.loadFromJson(step, undefined, null, true);
                        return stepData;
                    })
                .forEach(step => this.addStep(step));

            if (this.nextStepData !== undefined) {
            }
        }
        */

        this.loadFromJson(json, effects, undefined, undefined, undefined, track, midiEffects);
    }

    loadFromJson(json, effects, reversed=null, skipPolyphony=false, sequencerEngine, track, midiEffects) {
        this.on = json.on;

        if (json.id !== undefined) {
            this.id = json.id;
        }

        this.slide = json.slide;

        this.duration = json.duration === undefined ? null : json.duration;
        if (json.durationParameter !== undefined) {
            this.durationParameter.fromJson(json.durationParameter, sequencerEngine);
        }

        this.panning = json.panning === undefined ? null : json.panning;

        if (json.transpose !== undefined) {
            this.transpose = json.transpose;
        }
        this.transposeParameter.fromJson(json.transposeParameter, sequencerEngine);
        if (json.velocity !== undefined) {
            this.velocity = json.velocity;
        }
        this.velocityParameter.fromJson(json.velocityParameter, sequencerEngine);
        if (json.start !== undefined) {
            this.start = json.start;
        }
        this.startParameter.fromJson(json.startParameter, sequencerEngine);
        if (json.chop !== undefined) {
            this.chop= json.chop;
        }
        this.chopParameter.fromJson(json.chopParameter, sequencerEngine);
        this.chopDuration = json.chopDuration === undefined ? null : json.chopDuration;
        if (json.attack !== undefined) {
            this.attack = json.attack;
        }
        this.attackParameter.fromJson(json.attackParameter, sequencerEngine);
        if (json.release !== undefined) {
            this.release = json.release;
        }
        this.releaseParameter.fromJson(json.releaseParameter, sequencerEngine);

        this.slice = 'slice' in json ? json.slice : 0;
        this.palette = 'palette' in json ? json.palette : null;
        this.probability = 'probability' in json ? json.probability : 100;

        this.auxEventAParameter.fromJson(json.auxEventAParameter, sequencerEngine);
        this.auxEventBParameter.fromJson(json.auxEventBParameter, sequencerEngine);
        this.auxEventCParameter.fromJson(json.auxEventCParameter, sequencerEngine);
        this.auxEventDParameter.fromJson(json.auxEventDParameter, sequencerEngine);

        this.mutate = 'mutate' in json ? json.mutate : null;

        if (json.pitchAttack !== undefined) {
            this.pitchAttack = json.pitchAttack;
        }
        this.pitchAttackParameter.fromJson(json.pitchAttackParameter, sequencerEngine);
        if (json.pitchDecay !== undefined) {
            this.pitchDecay = json.pitchDecay;
        }
        this.pitchDecayParameter.fromJson(json.pitchDecayParameter, sequencerEngine);
        if (json.pitchSustain !== undefined) {
            this.pitchSustain = json.pitchSustain;
        }
        this.pitchSustainParameter.fromJson(json.pitchSustainParameter, sequencerEngine);
        if (json.pitchRelease !== undefined) {
            this.pitchRelease = json.pitchRelease;
        }
        this.pitchReleaseParameter.fromJson(json.pitchReleaseParameter, sequencerEngine);
        if (json.selectedRecording !== undefined) {
            this.selectedRecording = json.selectedRecording;
        }
        this.monophonicParameter.fromJson(json.monophonicParameter, sequencerEngine);
        if (json.monophonic !== undefined) {
            this.monophonic = json.monophonic;
        }
        this.selectedRecordingParameter.fromJson(json.selectedRecordingParameter, sequencerEngine);
        this.reverse = json.reverse === undefined ? reversed : json.reverse;

        if (json.effectAutomations !== undefined) {
            this.loadAutomations(json.effectAutomations, track, effects, midiEffects);
       }

        if ('polyphonicSteps'  in json && !skipPolyphony) {
            
            json.polyphonicSteps
                .map(
                    step => {
                        let stepData = new StepData();
                        stepData.stepNumber = this.stepNumber;
                        stepData.loadFromJson(step, undefined, null, true);
                        return stepData;
                    })
                .forEach(step => this.addStep(step));

            if (this.nextStepData !== undefined) {
            }
        }

        if (json.pLockedParameters) {
            // from the parameters find the shit we want
            this.pLockedParameterIds = json.pLockedParameters;
        }

        if (json.group && this.track) {
            this.group = this.track.getGroupForId(json.group);
            if (this.group) {
                if (!this.group.steps) {
                    this.group.steps = [];
                }
                this.group.steps.push(this);
            }
        }

        return this;
    }

    loadParameterLocks() {
        let ids = this.pLockedParameterIds;
        if (!ids) {
            return;
        }
        if (this.pLockedParameterIds.length > 0) {

            let parameters = [
                ...this.track.getAllParameters(),
                ...this.track.sequencerEngine.currentSubSequence.getAllParameters(),
                ...this.track.sequencerEngine.envelopes.flatMap(
                    (env) => env.parameters),
                ...this.track.sequencerEngine.lfos.flatMap(
                    ({lfo}) => lfo.parameters)];
            this.pLockedParameters = parameters.filter(
                p => ids.includes(p.id));
        }
    }

    clearAutomations() {
        this.automations.clear();
    }

    loadAutomations(automations, track, effects, midiEffects) {
        for (let i=0; i < automations.length; i++) {
            let automationJson = automations[i];
            let effectNumber = automationJson.effectNumber;
            let name = automationJson.parameterName;
            let value = automationJson.value;
            let effect = effects[effectNumber];
            let midiEffect = midiEffects[effectNumber];
            
            if (effect !== undefined) {
                // find the effect parameter given the name in the json
                this.findAutomationParameter(effect, name, effectNumber, value);
            }

            if (midiEffect !== undefined) {
                this.findAutomationParameter(midiEffect, name, effectNumber, value);
            }

            if (effectNumber === undefined) {
                this.findAutomationInstrumentParameter(track.instrument, automationJson, name, value);
            }
        }
    }

    // THIS IS NOT USED
    findAutomationParameter(effect, name, effectNumber,  value) {
        let parametersFound = effect.parameters.filter(p => name === p.name);
        if (parametersFound.length > 0) {
            let parameter = parametersFound[0];
            this.effectAutomations.set(parameter, new ParameterFuture(effectNumber, parameter, value ));
        }
    }

    // THIS IS NOT USED
    findAutomationInstrumentParameter(instrument, automationJson, name,  value) {
        let parameter = instrument.findParameter(automationJson, name);
        let automationContext = {... automationJson};
        delete automationContext.name;
        delete automationContext.value;
        delete automationContext.effectNumber;
        if (parameter !== undefined) {
            this.effectAutomations.set(parameter, new ParameterFuture(undefined, parameter, value, automationContext));
        }
    }

    loadDefaults() {
        this.on = false;
        this.reverse = false;
        this.mutate = 0;
        this.transpose = 0;
        this.duration = 1;
        this.velocity = 1.0;
        this.start = 0.0;
        this.chop = 1;
        this.chopDuration = 1;
        this.attack = 0;
        this.panning = 0;
        this.release= 15;
        this.slice = 0;
        this.probability = 100;
        this.auxEventA = 0;
        this.auxEventB = 0;
        this.auxEventC = 0;
        this.auxEventD = 0;

        this.pitchAttack = 0.01;
        this.pitchDecay = 0.01;
        this.pitchSustain = 0;
        this.pitchRelease = 0.01;
        this.monophonic = false;
        this.selectedRecording = 0;
    }

    clear() {
        this.on = false;
        this.reverse = null;
        this.transpose = null;
        this.panning = null;
        this.duration = null;
        this.velocity = null;
        this.start = null;
        this.chop = null;
        this.chopDuration = null;
        this.attack = null;
        this.release = null;
        this.pitchAttack= null;
        this.pitchDecay = null;
        this.pitchRelease= null;
        this.pitchSustain = null;
        this.selectedRecording = null;
        this.probability = null;
        this.transposeMatch = null;
        this.slice = null;
        this.polyphonicSteps = [];
        this.nextStepData = undefined;
        this.monophonic = null;
        this.mutate = null;
        this.auxEventA = null;
        this.auxEventB = null;
        this.auxEventC = null;
        this.auxEventD = null;
    }

    getCopy(skipPoly=false, copyId=true, shallowCopy=false, skipSequencerEngine=false, useCache=true, saveCopy=true) {
        var stepData = undefined;
        if (this.stepDataCopy !== undefined && useCache) {
            stepData = this.stepDataCopy;
        } else {
            stepData = skipSequencerEngine ?
                new StepData() :
                new StepData(this.sequencerEngine, this.mixSession);
        }
        if (saveCopy) {
            this.stepDataCopy = stepData;
        }
        stepData.stepNumber = this.stepNumber;
        if (copyId) {
            stepData.id = this.id;
        }
        stepData.track = this.track;
        stepData.group = this.group;
        stepData.position = this.position;
        stepData.on = this.on;
        stepData.slide = this.slide;
        stepData.effectAutomations = this.effectAutomations.getCopy();
        stepData.durationParameter.copyFrom(this.durationParameter, this.sequencerEngine, shallowCopy);
        stepData.chopParameter.copyFrom(this.chopParameter, this.sequencerEngine, shallowCopy);
        stepData.panningParameter.copyFrom(this.panningParameter, this.sequencerEngine, shallowCopy);
        stepData.transposeParameter.copyFrom(this.transposeParameter, this.sequencerEngine, shallowCopy);
        stepData.velocityParameter.copyFrom(this.velocityParameter, this.sequencerEngine, shallowCopy);
        stepData.startParameter.copyFrom(this.startParameter, this.sequencerEngine, shallowCopy);
        stepData.attackParameter.copyFrom(this.attackParameter, this.sequencerEngine, shallowCopy);
        stepData.releaseParameter.copyFrom(this.releaseParameter, this.sequencerEngine, shallowCopy);
        stepData.auxEventAParameter.copyFrom(this.auxEventAParameter, this.sequencerEngine, shallowCopy);
        stepData.auxEventBParameter.copyFrom(this.auxEventBParameter, this.sequencerEngine, shallowCopy);
        stepData.auxEventCParameter.copyFrom(this.auxEventCParameter, this.sequencerEngine, shallowCopy);
        stepData.auxEventDParameter.copyFrom(this.auxEventDParameter, this.sequencerEngine, shallowCopy);
        stepData.sliceParameter.copyFrom(this.sliceParameter, this.sequencerEngine, shallowCopy);
        stepData.transposeMatchParameter.copyFrom(this.transposeMatchParameter, this.sequencerEngine, shallowCopy);
        if (!skipPoly) {
            this.getPolyphony().map(step => step.getCopy(true, copyId, shallowCopy, skipSequencerEngine, useCache))
                .slice(1)
                .forEach(step => stepData.addStep(step));
        }
        stepData.pitchAttackParameter.copyFrom(this.pitchAttackParameter, this.sequencerEngine, shallowCopy);
        stepData.pitchDecayParameter.copyFrom(this.pitchDecayParameter, this.sequencerEngine, shallowCopy);
        stepData.pitchSustainParameter.copyFrom(this.pitchSustainParameter, this.sequencerEngine, shallowCopy);
        stepData.pitchReleaseParameter.copyFrom(this.pitchReleaseParameter, this.sequencerEngine, shallowCopy);
        stepData.selectedRecordingParameter.copyFrom(this.selectedRecordingParameter, this.sequencerEngine, shallowCopy);
        stepData.monophonicParameter.copyFrom(this.monophonicParameter, this.sequencerEngine, shallowCopy);
        stepData.paletteParameter.copyFrom(this.paletteParameter, this.sequencerEngine, shallowCopy);
        stepData.reverse = this.reverse;
        stepData.probability = this.probability;

        // copy over any plocks
        stepData.pLockedParameters = [... this.pLockedParameters];
        for (let p of stepData.pLockedParameters) {
            p.stepToValue[stepData.id] = p.stepToValue[this.id];
        }
        return stepData;
    }

    copyFrom(stepData, skipPolyphony=false) {
        if (!stepData) {
            return;
        }
        this.on = stepData.on;
        this.stepNumber = stepData.stepNumber;
        this.duration = stepData.durationParameter.get();
        this.panning = stepData.panningParameter.get();
        this.chop = stepData.chopParameter.get();
        this.chopDuration = stepData.chopDurationParameter.get();
        this.transpose= stepData.transposeParameter.get();
        this.velocity= stepData.velocityParameter.get();
        this.start = stepData.startParameter.get();
        this.attack = stepData.attackParameter.get();
        this.release = stepData.releaseParameter.get();
        this.slice = stepData.sliceParameter.get();
        this.pitchAttack= stepData.pitchAttackParameter.get();
        this.pitchDecay = stepData.pitchDecayParameter.get();
        this.pitchSustain = stepData.pitchSustainParameter.get();
        this.pitchRelease = stepData.pitchReleaseParameter.get();
        this.palette = stepData.paletteParameter.get();
        this.probability = stepData.probabilityParameter.get();
        this.selectedRecording = stepData.selectedRecordingParameter.get();
        this.transposeMatch = stepData.transposeMatchParameter.get();
        this.monophonic = stepData.monophonicParameter.get();
        this.reverse = stepData.reverse;

        if (!skipPolyphony) {
            stepData.getPolyphony()
                .slice(1)
                .map(step => step.getCopy())
                .forEach(step => this.addStep(step));
        }
    }

    getJson(skipPolyphony=false) {
        let json = {
            on : this.on,
            id: this.pLockedParameters && this.pLockedParameters.length > 0 ? this.id : undefined,
            slide: this.slide,
        };

        if (this.group) {
            json.group = this.group.id;
        }

        if (this.pLockedParameters && this.pLockedParameters.length > 0) {
            json.pLockedParameters = this.pLockedParameters.map(x => x.id);
        }

        if (this.reverse) {
            json.reverse = this.reverse;
        }

        if (this.durationParameter.lfos.length > 0 ||
            Object.keys(this.durationParameter.patternToValue).length > 0 ||
            this.durationParameter.get() !== null) {
            if (this.durationParameter.store) {
                json.durationParameter = this.durationParameter.getJson();
            }
        }

        if (this.durationParameter.get() !== null) {
            json.duration = this.duration;
        }

        if (this.probabilityParameter.get() !== null) {
            if (this.probability !== 100) {
                json.probability = this.probability;
            }
        }

        if (this.velocityParameter.get() !== null) {
            json.velocity = this.velocity;
        }

        if (this.panningParameter.get() !== null) {
            json.panning = this.panning;
        }

        if (this.chopParameter.lfos.length > 0 ||
            Object.keys(this.chopParameter.patternToValue).length > 0 ||
            this.chopParameter.get() !== null) {
            json.chopParameter = this.chopParameter.getJson();
        }


        if (this.transposeParameter.lfos.length > 0 ||
            Object.keys(this.transposeParameter.patternToValue).length > 0 ||
            this.transposeParameter.get() !== null) {
            json.transposeParameter = this.transposeParameter.getJson();
        }

        if (this.velocityParameter.lfos.length > 0 ||
            Object.keys(this.velocityParameter.patternToValue).length > 0 ||
            this.velocityParameter.get() !== null) {
           
            if (this.velocityParameter.store) {
                json.velocityParameter = this.velocityParameter.getJson();
            }
        }

        if (this.auxEventAParameter.get() !== null) {
            json.auxEventAParameter  = this.auxEventAParameter.getJson();
        }

        if (this.auxEventBParameter.get() !== null) {
            json.auxEventBParameter  = this.auxEventBParameter.getJson();
        }

        if (this.auxEventCParameter.get() !== null) {
            json.auxEventCParameter  = this.auxEventCParameter.getJson();
        }

        if (this.auxEventDParameter.get() !== null) {
            json.auxEventDParameter  = this.auxEventDParameter.getJson();
        }

        if (this.startParameter.lfos.length > 0 ||
            Object.keys(this.startParameter.patternToValue).length > 0 ||
            this.startParameter.get() !== null) {
           
            json.startParameter = this.startParameter.getJson();
        }

        if (this.attackParameter.lfos.length > 0 ||
            Object.keys(this.attackParameter.patternToValue).length > 0 ||
            this.attackParameter.get() !== null) {
            json.attackParameter = this.attackParameter.getJson();
        }

        if (this.attackParameter.get() !== null) {
            json.attack = this.attack;
        }

        if (this.releaseParameter.lfos.length > 0 ||
            Object.keys(this.releaseParameter.patternToValue).length > 0 ||
            this.releaseParameter.get() != null) {
            json.releaseParameter = this.releaseParameter.getJson();
        }

        if (this.slice) {
            json.slice = this.slice;
        }

        if (this.palette !== null) {
            json.palette  = this.palette;
        }

        if (this.mutate !== null) {
            json.mutate  = this.mutate;
        }

        if (this.pitchAttackParameter.lfos.length > 0 ||
            Object.keys(this.pitchAttackParameter.patternToValue).length > 0 ||
            this.pitchAttackParameter.get() != null) {
            json.pitchAttackParameter = this.pitchAttackParameter.getJson();
        }

        if (this.pitchDecayParameter.lfos.length > 0 ||
            Object.keys(this.pitchDecayParameter.patternToValue).length > 0 ||
            this.pitchDecayParameter.get() != null) {
            json.pitchDecayParameter = this.pitchDecayParameter.getJson();
        }

        if (this.pitchSustainParameter.lfos.length > 0 ||
            Object.keys(this.pitchSustainParameter.patternToValue).length > 0 ||
            this.pitchSustainParameter.get() != null) {
            json.pitchSustainParameter = this.pitchSustainParameter.getJson();
        }

        if (this.pitchReleaseParameter.lfos.length > 0 ||
            Object.keys(this.pitchReleaseParameter.patternToValue).length > 0 ||
            this.pitchReleaseParameter.get() != null) {
            json.pitchReleaseParameter = this.pitchReleaseParameter.getJson();
        }

        if (this.selectedRecordingParameter.lfos.length > 0 ||
            Object.keys(this.selectedRecordingParameter.patternToValue).length > 0 ||
            this.selectedRecordingParameter.get() != null) {
            json.selectedRecordingParameter = this.selectedRecordingParameter.getJson();
        }

        if (Object.keys(this.monophonicParameter.patternToValue).length > 0 ||
            this.monophonicParameter.get() != null) {
            json.monophonicParameter = this.monophonicParameter.getJson();
        }

        if (!skipPolyphony) {
            let poly = this.getPolyphony().slice(1).map(step => step.getJson(true));
            if (poly.length > 0) {
                json.polyphonicSteps = poly;
            }
        }

        if (this.effectAutomations.automations.length > 0) {
            json.effectAutomations = this.effectAutomations.automations
                .map(p => p.getJson());
        }
        return json;
    }
}

class EffectAutomations {
    constructor() {
        this.automations = [];
    }

    clear() {
        this.automations = [];
    }

    get(parameter) {
        let p = this.automations.filter(a => a.parameter === parameter);
        if (p.length > 0) {
            return p[0];
        }

        return undefined;
    }

    delete(parameter) {
        let index=-1;
        for (let i in this.automations) {
            if (this.automations[i].parameter === parameter) {
                index = i;
            }
        }
        if (index === -1) {
            return;
        }

        this.automations.splice(index, 1);
    }

    set(parameter, parameterFuture) {
        this.automations.push(parameterFuture);
    }

    trigger(time, duration) {
        this.automations.forEach(
            a => a.setAtTime(time, duration));
    }

    isEmpty() {
        return this.automations.length == 0;
    }

    forAudioEffects() {
        return this.automations
            .filter(x => x.parameter.context.effect)
            .filter(
            x => !x.parameter.context.effect.isMidiEffect());
    }

    forMidiEffects() {
        return this.automations
            .filter(x => x.parameter.context.effect)
            .filter(
            x => x.parameter.context.effect.isMidiEffect());
    }

    forInstruments() {
        return this.automations
            .filter(x => x.parameter.context.track)
            .filter(
                x => x.parameter.context.track.instrument !== undefined);
    }

    getCopy() {
        let automations = new EffectAutomations();
        automations.automations = this.automations
            .map(a => a.getCopy());
        return automations;
    }
}

const orNull = (obj) => {
    return obj === undefined ? null : obj;
}

export default StepData;
